import { z } from 'zod'

/** Track coordinates and sizes are authored on this grid (ARCHITECTURE §2.6). */
export const TRACK_GRID_PX = 32

const onGrid = z
  .number()
  .int()
  .refine((v) => v % TRACK_GRID_PX === 0, `must be a multiple of ${TRACK_GRID_PX} (track grid)`)
const size = onGrid.refine((v) => v > 0, 'must be > 0')
const ticks = z.number().int().positive()
const phase = z.number().int().min(0)

const box = { x: onGrid, y: onGrid, w: size, h: size }

/**
 * *Visual* fields (MAP_01_SKY_MEADOW §2): validated here, ignored by the sim, read by the
 * renderer. `supports` is `"auto"`, `"none"` or x offsets from the segment's `x`; `"none"` only
 * on a `raft` (it floats on the water, MAP_02 §2).
 */
export const GROUND_STYLES = [
  'stone_slab',
  'boardwalk',
  'grass_ledge',
  'log',
  'rope_bridge',
  'raft',
] as const
const visual = {
  style: z.enum(GROUND_STYLES).optional(),
  supports: z.union([z.enum(['auto', 'none']), z.array(z.number().int().min(0))]).optional(),
}

/** Hop height: an obstacle is never taller than one jump clears (MAP_01 §1.5). */
export const OBSTACLE_MAX_H_PX = 96

/** Shard trail layouts; `compileTrack` expands a trail into coins. */
export const SHARD_SHAPES = ['line', 'arc', 'grid'] as const

/** `(x, y)` is the pivot; the blade hangs down and swings ±`amplitudeDeg`. */
const blade = {
  x: onGrid,
  y: onGrid,
  lengthPx: z.number().int().positive(),
  amplitudeDeg: z.number().positive().max(90),
  periodTicks: ticks,
  phaseTicks: phase,
}

/** `y` is the block's top while waiting; it drops `dropPx`. */
const block = { ...box, dropPx: z.number().int().positive(), periodTicks: ticks, phaseTicks: phase }

/** Pickup position: top-left of the pickup box; `y` may be any integer. */
const pickup = { x: onGrid, y: z.number().int() }

/** Every track component. `x`/`y` is the top-left corner (Y grows down) unless noted. */
export const segmentSchema = z.discriminatedUnion('type', [
  /** The walkway: a solid, authored thin (h 32–48) with open air below on Sky Meadow. */
  z.strictObject({ type: z.literal('ground'), ...box, ...visual }),
  /** Documentation only: a pit with no ground. Compiles to nothing. */
  z.strictObject({ type: z.literal('gap'), x: onGrid, w: size }),
  /** `y` is the top of the ramp's box (D-027). */
  z.strictObject({ type: z.literal('ramp'), ...box, dir: z.enum(['up', 'down']), ...visual }),
  z.strictObject({
    type: z.literal('moving_platform'),
    ...box,
    dx: onGrid,
    dy: onGrid,
    periodTicks: ticks,
    ...visual,
  }),
  z.strictObject({ type: z.literal('crumbling_platform'), ...box, ...visual }),
  /** One-way: jump up through it, land on its top (upper tiers, shortcut slabs, log beams). */
  z.strictObject({ type: z.literal('platform'), ...box, ...visual }),
  /** Solid block on the walkway to hop over or stand on; never taller than one jump. */
  z.strictObject({
    type: z.literal('obstacle'),
    ...box,
    h: size.refine((v) => v <= OBSTACLE_MAX_H_PX, `must be at most ${OBSTACLE_MAX_H_PX}`),
    kind: z.enum(['crate', 'barrel_row', 'rock', 'log_pile']),
  }),
  /** Line zones: `y` is the ground top they sit on. */
  z.strictObject({ type: z.literal('bounce_pad'), x: onGrid, y: onGrid, w: size }),
  z.strictObject({ type: z.literal('speed_strip'), x: onGrid, y: onGrid, w: size }),
  z.strictObject({ type: z.literal('slow_strip'), x: onGrid, y: onGrid, w: size }),
  /** Feet inside the box (inclusive) are in mud. */
  z.strictObject({ type: z.literal('mud'), ...box }),
  z.strictObject({ type: z.literal('swinging_blade'), ...blade }),
  /** Same rules as `swinging_blade`, drawn as a spiked log on two ropes. */
  z.strictObject({ type: z.literal('log_ram'), ...blade }),
  /** `y` is the ground top the spikes rise from. */
  z.strictObject({
    type: z.literal('spike_strip'),
    x: onGrid,
    y: onGrid,
    w: size,
    periodTicks: ticks,
    activeTicks: ticks,
    phaseTicks: phase,
  }),
  /** `y` is the ground top; sprung stakes rise from it (closed → rattling → sprung). */
  z.strictObject({
    type: z.literal('trapdoor'),
    x: onGrid,
    y: onGrid,
    w: size,
    periodTicks: ticks,
    phaseTicks: phase,
  }),
  /** Static, always harmful; `y` is the ground top they stand on. */
  z.strictObject({ type: z.literal('stake_barricade'), x: onGrid, y: onGrid, w: size }),
  z.strictObject({ type: z.literal('spiked_stone'), x: onGrid, y: onGrid }),
  /**
   * A barrel rolls left from a gate at `x + w` along the run `[x, x + w]` (MAP_02 §2); `y` is the
   * ground top it rolls on.
   */
  z.strictObject({
    type: z.literal('barrel_roll'),
    x: onGrid,
    y: onGrid,
    w: size,
    periodTicks: ticks,
    phaseTicks: phase,
  }),
  z.strictObject({ type: z.literal('falling_block'), ...block }),
  /** Same rules as `falling_block`, drawn as a netted boulder with a shadow. */
  z.strictObject({ type: z.literal('boulder_drop'), ...block }),
  z.strictObject({ type: z.literal('crate'), ...pickup }),
  /** Same as `crate` (the crystal item box). */
  z.strictObject({ type: z.literal('item_box'), ...pickup }),
  z.strictObject({ type: z.literal('coin'), ...pickup }),
  /**
   * Coins along a line, an arc (`arcHeight` px up at the middle) or a grid (`rows` rows, the
   * same `spacing` both ways). `(x, y)` is the first coin's box top-left.
   */
  z.strictObject({
    type: z.literal('shard_trail'),
    x: onGrid,
    y: z.number().int(),
    count: z.number().int().min(1).max(64),
    spacing: size,
    shape: z.enum(SHARD_SHAPES),
    rows: z.number().int().min(1).max(8).optional(),
    arcHeight: z.number().int().min(0).optional(),
  }),
  /** *Visual* only: a prop by manifest id, never collides; `fg` in front of runners. */
  z.strictObject({
    type: z.literal('decor'),
    id: z.string().regex(/^[a-z0-9_]+$/),
    x: z.number().int(),
    y: z.number().int(),
    layer: z.enum(['fg', 'mid']),
    flip: z.boolean().optional(),
    scale: z.number().positive().max(4).optional(),
  }),
  /** Respawn point; `y` is the feet position (D-021). */
  z.strictObject({ type: z.literal('checkpoint'), x: onGrid, y: onGrid }),
])

export type SegmentDef = z.infer<typeof segmentSchema>
export type SegmentType = SegmentDef['type']
export type SegmentOf<T extends SegmentType> = Extract<SegmentDef, { type: T }>

/** Kinds that support or block a runner; a `gap` must not overlap any of them. */
const SUPPORT_TYPES: ReadonlySet<SegmentType> = new Set(['ground', 'ramp'])

/** Where a hand-placed picture draws: `back` behind the runners, `front` in front of them. */
export const ART_LAYERS = ['back', 'front'] as const

/**
 * *Visual* only (O-47): one hand-placed picture of the walkway kit, saved from Tiled. `src` is
 * the image under `apps/client/public/assets/`; `x`/`y` its top-left, turned `rotation` degrees
 * clockwise around that corner; `flip` mirrors it. Never collides and the sim never reads it.
 */
export const artPieceSchema = z.strictObject({
  layer: z.enum(ART_LAYERS),
  src: z
    .string()
    .regex(/^[a-z0-9_-]+(\/[a-z0-9_.-]+)*\.(webp|png)$/, 'must be an image path under assets/')
    .refine((s) => !s.includes('..'), 'must not leave assets/'),
  x: z.number(),
  y: z.number(),
  w: z.number().positive(),
  h: z.number().positive(),
  rotation: z.number().min(-360).max(360).optional(),
  flip: z.boolean().optional(),
})

export type ArtPiece = z.infer<typeof artPieceSchema>

export const trackSchema = z
  .strictObject({
    id: z.string().regex(/^[a-z0-9_]+$/, 'must be lowercase letters, digits and _'),
    name: z.string().min(1),
    /** *Visual*: the art set the renderer draws this map with (one ground theme per map). */
    theme: z
      .string()
      .regex(/^[a-z0-9_]+$/)
      .optional(),
    targetSeconds: z.number().int().positive(),
    spawn: z.strictObject({ x: onGrid, y: onGrid }),
    finishX: onGrid,
    killY: onGrid,
    /** *Visual*: the water surface the renderer draws (and the camera floor, D-080). */
    waterY: z.number().int().optional(),
    segments: z.array(segmentSchema).min(1),
    /**
     * *Visual*: hand-placed walkway pictures (O-47). When present, the renderer draws these
     * instead of the automatic ground / platform / ramp / support / bounce-cap art, so the
     * boxes are pure hitboxes; moving and crumbling platforms keep their own art.
     */
    art: z.array(artPieceSchema).optional(),
  })
  .superRefine((t, ctx) => {
    const segs = t.segments
    // Decor may sit past the finish (the arch, the run-out); nothing else may.
    const lastStart = Math.max(...segs.filter((s) => s.type !== 'decor').map((s) => s.x))
    if (t.finishX <= lastStart) {
      ctx.addIssue({
        code: 'custom',
        path: ['finishX'],
        message: `must be past the last segment start (${lastStart})`,
      })
    }

    let prevCheckpoint: number | null = null
    segs.forEach((s, i) => {
      if (s.type !== 'checkpoint') return
      if (prevCheckpoint === null && s.x !== t.spawn.x) {
        ctx.addIssue({
          code: 'custom',
          path: ['segments', i, 'x'],
          message: `first checkpoint must be at spawn.x (${t.spawn.x})`,
        })
      }
      if (prevCheckpoint !== null && s.x <= prevCheckpoint) {
        ctx.addIssue({
          code: 'custom',
          path: ['segments', i, 'x'],
          message: `checkpoints must be strictly increasing (previous at ${prevCheckpoint})`,
        })
      }
      prevCheckpoint = s.x
    })
    if (prevCheckpoint === null) {
      ctx.addIssue({ code: 'custom', path: ['segments'], message: 'needs a checkpoint at spawn.x' })
    }

    segs.forEach((s, i) => {
      if ('supports' in s && s.supports === 'none' && s.style !== 'raft') {
        ctx.addIssue({
          code: 'custom',
          path: ['segments', i, 'supports'],
          message: 'supports "none" is only for style "raft" (anything else must be held up)',
        })
      }
    })

    segs.forEach((g, i) => {
      if (g.type !== 'gap') return
      segs.forEach((s, j) => {
        if (!SUPPORT_TYPES.has(s.type) || !('w' in s)) return
        if (s.x < g.x + g.w && s.x + s.w > g.x) {
          ctx.addIssue({
            code: 'custom',
            path: ['segments', i],
            message: `gap overlaps ${s.type} at segments.${j}`,
          })
        }
      })
    })
  })

export type TrackFile = z.input<typeof trackSchema>
export type TrackDef = z.infer<typeof trackSchema>
